import React, { useEffect, useState } from "react";
import { Ticket, X, CheckCircle2, AlertCircle, Loader2, Link2 } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import HowThisWorks from "@/components/admin/HowThisWorks";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function DataSourceTickets() {
    const [platform, setPlatform] = useState("Zendesk");
    const [status, setStatus] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState({ subdomain: "", email: "", api_token: "" });
    const [saving, setSaving] = useState(false);

    const load = () =>
        adminApi
            .get("/api/admin/integrations/zendesk")
            .then((r) => setStatus(r.data.zendesk));

    useEffect(() => {
        load();
    }, []);

    const connected = status?.status === "connected";
    const pending = status?.status === "pending";
    const failed = status?.status === "failed";

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.subdomain.trim()) {
            toast.error("Zendesk subdomain is required");
            return;
        }
        setSaving(true);
        try {
            const { data } = await adminApi.post(
                "/api/admin/integrations/zendesk/connect",
                form
            );
            setStatus(data.zendesk);
            if (data.zendesk.status === "connected") {
                toast.success("Zendesk connected");
            } else if (data.zendesk.status === "pending") {
                toast.info(
                    "Saved. Add an API token to verify and start importing tickets."
                );
            } else {
                toast.error(
                    `Connection failed: ${data.zendesk.last_error || "unknown error"}`
                );
            }
            setModalOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to connect");
        } finally {
            setSaving(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Zendesk?")) return;
        await adminApi.post("/api/admin/integrations/zendesk/disconnect");
        toast.success("Disconnected");
        setForm({ subdomain: "", email: "", api_token: "" });
        load();
    };

    return (
        <div className="p-10 max-w-5xl" data-testid="page-tickets-source">
            <PageHeader
                eyebrow="Data sources"
                title="Tickets"
                description="Use historical support tickets from Zendesk or Salesforce to train Priya on real guest issues."
            />

            <div className="bg-white border border-stone-200 rounded-xl p-8 max-w-3xl mb-6">
                <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-xl bg-orchid-700/10 text-orchid-700 flex items-center justify-center shrink-0">
                        <Ticket size={22} />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-2xl text-orchid-700 mb-1">Add tickets</h2>
                        <p className="text-sm text-stone-600 mb-5">
                            Connect a support platform to import resolved tickets as training data.
                        </p>

                        <label className="text-[11px] uppercase tracking-[0.2em] text-stone-500 mb-2 block">
                            Platform
                        </label>
                        <div className="flex items-center gap-3 mb-5">
                            <select
                                value={platform}
                                onChange={(e) => setPlatform(e.target.value)}
                                data-testid="tickets-platform"
                                className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white focus:outline-none focus:border-orchid-700"
                            >
                                <option>Zendesk</option>
                                <option>Salesforce</option>
                                <option>Freshdesk</option>
                            </select>
                            {platform === "Zendesk" ? (
                                connected ? (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-sage-500/15 text-sage-700 border border-sage-500/30"
                                        data-testid="zd-status"
                                    >
                                        <CheckCircle2 size={11} /> Connected
                                        {status?.account_name ? ` · ${status.account_name}` : ""}
                                    </span>
                                ) : pending ? (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-gold-500/15 text-gold-700 border border-gold-500/30"
                                        data-testid="zd-status"
                                    >
                                        Pending
                                    </span>
                                ) : failed ? (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200"
                                        data-testid="zd-status"
                                    >
                                        <AlertCircle size={11} /> Failed
                                    </span>
                                ) : (
                                    <span
                                        className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-stone-100 text-stone-600 border border-stone-200"
                                        data-testid="zd-status"
                                    >
                                        Not connected
                                    </span>
                                )
                            ) : (
                                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-stone-100 text-stone-600 border border-stone-200">
                                    Coming soon
                                </span>
                            )}
                        </div>

                        {platform === "Zendesk" && status && status.subdomain && (
                            <div className="text-xs text-stone-600 mb-4 bg-stone-50 border border-stone-200 rounded-md p-3 space-y-0.5">
                                <div>
                                    <strong>Subdomain:</strong> {status.subdomain}.zendesk.com
                                </div>
                                {status.email && (
                                    <div>
                                        <strong>Account email:</strong> {status.email}
                                    </div>
                                )}
                                {status.api_token_last4 && (
                                    <div>
                                        <strong>API token:</strong> ••••{status.api_token_last4}
                                    </div>
                                )}
                                {status.last_error && (
                                    <div className="text-red-700 mt-1">
                                        Last error: {status.last_error}
                                    </div>
                                )}
                            </div>
                        )}

                        {platform === "Zendesk" ? (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setForm({
                                            subdomain: status?.subdomain || "",
                                            email: status?.email || "",
                                            api_token: "",
                                        });
                                        setModalOpen(true);
                                    }}
                                    data-testid="tickets-connect"
                                    className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors"
                                >
                                    <Link2 size={14} />
                                    {connected || pending ? "Reconfigure Zendesk" : "Connect Zendesk"}
                                </button>
                                {(connected || pending || failed) && (
                                    <button
                                        onClick={disconnect}
                                        className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                                    >
                                        Disconnect
                                    </button>
                                )}
                            </div>
                        ) : (
                            <button
                                disabled
                                data-testid="tickets-connect"
                                className="inline-flex items-center gap-2 bg-stone-200 text-stone-500 text-sm font-medium px-5 py-2.5 rounded-md cursor-not-allowed"
                            >
                                {platform} integration — Coming soon
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <HowThisWorks
                steps={[
                    "Click Connect Zendesk and enter your Zendesk subdomain (the part before .zendesk.com).",
                    "Add an account email + API token to verify the connection (find tokens under Zendesk Admin Centre → Apps & integrations → API).",
                    "Pick which queues / tags to import (resolved tickets work best).",
                    "Priya turns each ticket into a Q&A pair and trains on the corpus.",
                ]}
            />

            {/* Modal */}
            {modalOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={() => setModalOpen(false)}
                    data-testid="zd-modal"
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-heading text-xl text-orchid-700">
                                Connect Zendesk
                            </h3>
                            <button
                                onClick={() => setModalOpen(false)}
                                className="text-stone-400 hover:text-stone-700"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-xs text-stone-500 mb-5">
                            Find your API token in Zendesk Admin Centre → Apps &amp; integrations → API.
                            We store the token encrypted and only use it to import resolved tickets.
                        </p>
                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 block">
                                    Subdomain
                                </label>
                                <div className="flex items-center">
                                    <input
                                        value={form.subdomain}
                                        onChange={(e) =>
                                            setForm({ ...form, subdomain: e.target.value })
                                        }
                                        placeholder="orchidhotels"
                                        data-testid="zd-subdomain"
                                        className="flex-1 px-3 py-2 rounded-l-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                    />
                                    <div className="px-3 py-2 border border-l-0 border-stone-200 rounded-r-md text-xs bg-stone-50 text-stone-600">
                                        .zendesk.com
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 block">
                                    Account email
                                </label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({ ...form, email: e.target.value })
                                    }
                                    placeholder="helpdesk@orchidhotel.com"
                                    data-testid="zd-email"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 block">
                                    API token <span className="text-stone-400">(optional, recommended)</span>
                                </label>
                                <input
                                    type="password"
                                    value={form.api_token}
                                    onChange={(e) =>
                                        setForm({ ...form, api_token: e.target.value })
                                    }
                                    placeholder="••••••••••••"
                                    data-testid="zd-token"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                                <p className="text-[11px] text-stone-500 mt-1">
                                    Without a token we&apos;ll save the connection as <em>pending</em> until you verify it.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !form.subdomain.trim()}
                                    data-testid="zd-submit"
                                    className="text-sm font-medium px-4 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                    {saving ? (
                                        <Loader2 size={13} className="animate-spin" />
                                    ) : null}
                                    {saving ? "Connecting…" : "Save & verify"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
